// "My brand" [Mi marca] (M14): name, contact line, logo, 5 colours, heading and body font, price format.
// Saved in this browser and applied to every template (and to the ad that is open).
import { useEffect, useState } from 'react';
import { useT, type MessageKey } from '../../../i18n';
import {
  DEFAULT_BRAND,
  hexRgb,
  loadBrand,
  logoFromFile,
  rgbHex,
  saveBrand,
  type BrandProfile,
} from '../../ad/brand';
import { applyBrandToAd, useAdUi } from '../../ad/actions';
import { FONT_FAMILIES } from '../../text/fontCatalog';
import { formatPrice } from '../../ad/fields';
import d from '../../../ui/dialogs/Dialog.module.css';
import s from './Ad.module.css';

const COLORS: { key: keyof BrandProfile['colors']; label: MessageKey }[] = [
  { key: 'primary', label: 'brandPrimary' },
  { key: 'secondary', label: 'brandSecondary' },
  { key: 'accent', label: 'brandAccent' },
  { key: 'background', label: 'brandBackground' },
  { key: 'text', label: 'brandText' },
];

export function BrandDialog() {
  const t = useT();
  const open = useAdUi((a) => a.brandOpen);
  const [b, setB] = useState<BrandProfile | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (open) void loadBrand().then(setB);
  }, [open]);
  useEffect(() => {
    if (!b?.logo) return setLogoUrl(null);
    const u = URL.createObjectURL(new Blob([b.logo.bytes as Uint8Array<ArrayBuffer>], { type: b.logo.type }));
    setLogoUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [b?.logo]);
  if (!open || !b) return null;
  const close = () => useAdUi.setState({ brandOpen: false });
  const set = (p: Partial<BrandProfile>) => setB({ ...b, ...p });
  const fontSelect = (key: 'headingFont' | 'bodyFont', testId: string) => (
    <select data-testid={testId} value={b[key]} onChange={(e) => set({ [key]: e.target.value })}>
      {FONT_FAMILIES.map((f) => (
        <option key={f.family} value={f.family}>
          {f.family}
        </option>
      ))}
    </select>
  );
  return (
    <div
      className={d.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={t('adMyBrand')}
      data-testid="brand-dialog"
    >
      <div className={d.dialog} style={{ maxWidth: 560, maxHeight: '90vh', overflow: 'auto' }}>
        <h2 style={{ marginTop: 0 }}>{t('adMyBrand')}</h2>
        <p className={s.small}>{t('brandIntro')}</p>
        <label className={s.field}>
          <span>{t('brandName')}</span>
          <input
            type="text"
            data-testid="brand-name"
            value={b.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </label>
        <label className={s.field}>
          <span>{t('brandContact')}</span>
          <input
            type="text"
            data-testid="brand-contact"
            value={b.contact}
            placeholder={t('brandContactHint')}
            onChange={(e) => set({ contact: e.target.value })}
          />
        </label>
        <div className={s.field}>
          <span>{t('brandLogo')}</span>
          <div className={s.logoBox}>
            {logoUrl && <img src={logoUrl} alt="" />}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              data-testid="brand-logo"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (f) set({ logo: await logoFromFile(f) });
              }}
            />
            {b.logo && <button onClick={() => set({ logo: null })}>{t('brandLogoRemove')}</button>}
          </div>
        </div>
        <div className={s.field}>
          <span>{t('brandColors')}</span>
          <div className={s.swatchRow}>
            {COLORS.map((c) => (
              <label key={c.key}>
                <input
                  type="color"
                  data-testid={`brand-color-${c.key}`}
                  value={rgbHex(b.colors[c.key])}
                  onChange={(e) => set({ colors: { ...b.colors, [c.key]: hexRgb(e.target.value) } })}
                />
                {t(c.label)}
              </label>
            ))}
          </div>
        </div>
        <div className={s.pair}>
          <label className={s.field}>
            <span>{t('brandHeadingFont')}</span>
            {fontSelect('headingFont', 'brand-heading-font')}
          </label>
          <label className={s.field}>
            <span>{t('brandBodyFont')}</span>
            {fontSelect('bodyFont', 'brand-body-font')}
          </label>
        </div>
        <div className={s.pair}>
          <label className={s.field}>
            <span>{t('brandCurrency')}</span>
            <input
              type="text"
              maxLength={4}
              value={b.currency.symbol}
              onChange={(e) => set({ currency: { ...b.currency, symbol: e.target.value } })}
            />
          </label>
          <label className={s.field}>
            <span>{t('brandDecimals')}</span>
            <select
              value={b.currency.decimals}
              onChange={(e) =>
                set({ currency: { ...b.currency, decimals: Number(e.target.value) as 0 | 2 } })
              }
            >
              <option value={2}>45.00</option>
              <option value={0}>45</option>
            </select>
          </label>
        </div>
        <label>
          <input
            type="checkbox"
            checked={b.currency.after}
            onChange={(e) => set({ currency: { ...b.currency, after: e.target.checked } })}
          />{' '}
          {t('brandSymbolAfter')}
        </label>
        <div className={s.small}>{t('adPriceShows', { p: formatPrice(45, b.currency) })}</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button
            className={`${s.big} ${s.primary}`}
            data-testid="brand-save"
            onClick={async () => {
              await saveBrand(b);
              close();
              await applyBrandToAd();
            }}
          >
            {t('brandSave')}
          </button>
          <button
            className={s.big}
            onClick={() => setB({ ...DEFAULT_BRAND, logo: b.logo, name: b.name, contact: b.contact })}
          >
            {t('brandDefaults')}
          </button>
          <button className={s.big} onClick={close}>
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
